import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  Card,
  Input,
  Label,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Pencil, Trash } from 'phosphor-react'

import { productService } from 'api/services/product'
import { Wrapper } from 'components'
import { ProductCategory } from 'api/types/product'
import { sanitizeSlug } from 'utils/sanitizeString'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Obrigatório'),
  slug: Yup.string(),
})

export const ProductCategories = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [tableData, setTableData] = useState<ProductCategory[] | null>(null)

  const getCategories = async () => {
    setIsLoading(true)
    try {
      const response = await productService.getCategories()

      setTableData(response as ProductCategory[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as categorias.')
    }
  }

  const createCategory = async (name: string, slug: string) => {
    try {
      await productService.createCategory({ name, slug })
      getCategories()
      setShowCreateModal(false)
      toast.success('Categoria criada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível criar a categoria.')
    }
  }

  const editCategory = async () => {
    if (!selected) return
    try {
      await productService.changeCategory({
        id: selected,
        name: editFormik.values.name,
        slug: editFormik.values.slug,
      })
      getCategories()
      setShowEditModal(false)
      toast.success('Categoria editada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível editar a categoria.')
    }
  }

  const deleteCategory = async () => {
    if (!selected) return
    try {
      await productService.deleteCategory({
        id: selected,
      })
      setSelected(null)
      setShowDeleteModal(false)
      getCategories()
      toast.success('Categoria apagada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar a categoria.')
    }
  }

  const createFormik = useFormik({
    initialValues: {
      name: '',
      slug: '',
      productList: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      createCategory(values.name, values.slug)
    },
  })

  const editFormik = useFormik({
    initialValues: {
      name: '',
      slug: '',
      productList: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async () => {
      editCategory()
    },
  })

  useEffect(() => {
    getCategories()
  }, [])

  if (!tableData || !Array.isArray(tableData) || tableData.length === 0)
    return <></>

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Categorias de Produtos</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => setShowCreateModal(true)}
          >
            Adicionar Nova Categoria
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Produtos</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData &&
                  tableData.map((category: ProductCategory, index: number) => (
                    <TableRow
                      key={`${category.id}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {category.name}
                      </TableCell>
                      <TableCell className="normal-case">
                        {category.slug}
                      </TableCell>
                      <TableCell>
                        {category.productList
                          ? category.productList
                              .map(product => product.product)
                              .join(', ')
                          : 'Nenhum Produto'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              setSelected(category.id)
                              editFormik.setFieldValue('name', category.name)
                              editFormik.setFieldValue('slug', category.slug)
                              setShowEditModal(true)
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            onClick={() => {
                              setSelected(category.id)
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <div className="space-y-1">
              <ModalTitle>Nova Categoria</ModalTitle>
              <ModalDescription>
                Preencha os campos para adicionar uma categorias
              </ModalDescription>
              <form onSubmit={createFormik.handleSubmit}>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>Dados da Categoria</Label>
                    <Input
                      name="name"
                      placeholder="Nome da Categoria"
                      onChange={e => {
                        createFormik.setFieldValue(
                          'name',
                          e.currentTarget.value,
                        )
                        const slug = sanitizeSlug(e.currentTarget.value)
                        createFormik.setFieldValue('slug', slug)
                      }}
                      type="text"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      name="s"
                      placeholder="Nome da Categoria"
                      type="text"
                      value={createFormik.values.slug}
                      disabled={true}
                    />
                  </div>
                </div>
              </form>
            </div>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => createFormik.handleSubmit()}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showEditModal} onOpenChange={setShowEditModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <div className="space-y-1">
              <ModalTitle>Editar Categoria</ModalTitle>
              <ModalDescription className="pb-6">
                Category1 Preencha os campos para editar uma categoria
              </ModalDescription>
              <form onSubmit={editFormik.handleSubmit}>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>Dados da Categoria</Label>
                    <Input
                      name="name"
                      placeholder="Nome da Categoria"
                      value={editFormik.values.name}
                      onChange={e => {
                        editFormik.setFieldValue('name', e.currentTarget.value)
                        const slug = sanitizeSlug(e.currentTarget.value)
                        editFormik.setFieldValue('slug', slug)
                      }}
                      type="text"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      name="s"
                      placeholder="Nome da Categoria"
                      type="text"
                      value={editFormik.values.slug}
                      disabled={true}
                    />
                  </div>
                </div>
              </form>
            </div>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => editFormik.handleSubmit()}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Categoria</ModalTitle>
            <ModalDescription>
              Quer apagar esta categoria?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os produtos que estavam associados a esta categoria irão
                ficar sem categoria.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteCategory()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
